import { useCallback, useEffect, useState } from 'react';

/** Provides a mechanism to require the user to sign in with google before viewing the dev site */
export function useAuthInDev(): AuthResult {
  const isDev = Config.Shared.STAGE === 'dev';
  const isFirebaseDeployment = Config.buildPlatform === 'firebase';
  const isAuthenticationRequired = isDev && isFirebaseDeployment;
  const [isAuthenticated, setIsAuthenticated] = useState(!isAuthenticationRequired);
  const [wasAuthenticationChecked, setWasAuthenticationChecked] = useState(
    !isAuthenticationRequired
  );

  const authenticateWithGoogle = useCallback(async () => {
    if (!isAuthenticated) {
      try {
        const { GoogleAuthService } = await import('services/auth/google');

        await GoogleAuthService.authenticate();
        setIsAuthenticated(true);
      } catch (err) {
        if (isDev) {
          console.error(err);
        }
      }
    }
  }, [isAuthenticated, isDev]);

  const authenticateWithEmailAndPassword = useCallback(
    async (email: string, password: string) => {
      if (!isAuthenticated) {
        try {
          const { EmailPasswordAuthService } = await import('services/auth/emailPassword');

          await EmailPasswordAuthService.authenticate({ email, password });
          setIsAuthenticated(true);
        } catch (err) {
          if (isDev) {
            console.error(err);
          }

          throw err;
        }
      }
    },
    [isAuthenticated, isDev]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      import('services/auth/emailPassword')
        .then(async ({ EmailPasswordAuthService }) => {
          const isAuthenticated = await EmailPasswordAuthService.isAuthenticated();

          setWasAuthenticationChecked(true);
          setIsAuthenticated(isAuthenticated);
        })
        .catch(err => {
          if (isDev) {
            console.error(err);
          }
        });
    }
  }, [isAuthenticated, isDev]);

  return {
    isAuthenticated,
    authenticateWithGoogle,
    authenticateWithEmailAndPassword,
    wasAuthenticationChecked,
  };
}
interface AuthResult {
  /** Whether or not the user is authenticated */
  isAuthenticated: boolean;
  /** Redirects the user to the google signin page if they aren't authenticated */
  authenticateWithGoogle: () => Promise<void>;
  /**
   * Checks the user's credentials against the list of users in firebase. If there's a match, the user will be authenticated
   *
   * @param email - The email which identifies the user
   * @param password - The user's password
   */
  authenticateWithEmailAndPassword: (email: string, password: string) => Promise<void>;
  /** Whether or not the initial authentication check has been completed */
  wasAuthenticationChecked: boolean;
}
